'use client';

import { c as _c } from "react/compiler-runtime";
import type { ShopifyImage } from '@repo/shopify';
import Link from 'next/link';
import { useMemo } from 'react';
import { ShopifyImage as ShopifyImg } from '@/components/ui/shopify-image';
import { buildVariantSearchParams, getColorFromAltTag, type ProductVariantSelectionLike, resolveSelectedVariant } from './product-option-utils';
interface StyleSibling {
  handle: string;
  title: string;
  productType: string;
  /** Tile label override from a `style-label:` tag; falls back to productType. */
  styleLabel?: string | null;
  featuredImage: ShopifyImage | null;
  colorValues: string[];
  images: ShopifyImage[];
  variants: ProductVariantSelectionLike[];
  discountPercent: number | null;
  /** Keep this sibling visible even when its colorway differs from the selection. */
  keepAcrossColors?: boolean;
}
interface ProductStyleSelectorProps {
  currentHandle: string;
  siblings: StyleSibling[];
  selectedOptions: Record<string, string>;
}

/**
 * Find the best image for a sibling that matches the selected color,
 * using the `#color_` alt tag convention. Falls back to featuredImage.
 */
function normalizeColorForComparison(color: string): string {
  return color.toLowerCase().replace(/[\s\-_]+/g, '');
}
function getColorMatchedImage(sibling: StyleSibling, selectedColor: string | null): ShopifyImage | null {
  if (selectedColor && sibling.images.length > 0) {
    const normalizedColor = normalizeColorForComparison(selectedColor);
    const matched = sibling.images.find(img => {
      const imageColor = getColorFromAltTag(img.altText);
      if (!imageColor) return false;
      return normalizeColorForComparison(imageColor) === normalizedColor;
    });
    if (matched) return matched;
  }
  return sibling.featuredImage;
}
function isBundleSibling(sibling: StyleSibling) {
  return sibling.productType === 'Apparel Bundle';
}

/**
 * Build the href for a sibling style link, preserving the closest matching variant.
 */
function buildStyleHref(handle: string, variants: ProductVariantSelectionLike[], selectedOptions: Record<string, string>): string {
  const params = buildVariantSearchParams(resolveSelectedVariant(variants, selectedOptions));
  const query = params.toString();
  return query ? `/products/${handle}?${query}` : `/products/${handle}`;
}
export function ProductStyleSelector(t0) {
  const $ = _c(19);
  const {
    currentHandle,
    siblings,
    selectedOptions
  } = t0;
  const colorKey = Object.keys(selectedOptions).find(_temp);
  const selectedColor = colorKey ? selectedOptions[colorKey] ?? null : null;
  let t1;
  if ($[0] !== selectedColor || $[1] !== siblings) {
    let candidates = siblings;
    if (selectedColor) {
      let t2;
      if ($[3] !== selectedColor) {
        t2 = normalizeColorForComparison(selectedColor);
        $[3] = selectedColor;
        $[4] = t2;
      } else {
        t2 = $[4];
      }
      const normalizedColor = t2;
      let t3;
      if ($[5] !== normalizedColor) {
        t3 = s => s.colorValues.some(cv => normalizeColorForComparison(cv) === normalizedColor);
        $[5] = normalizedColor;
        $[6] = t3;
      } else {
        t3 = $[6];
      }
      const colorFiltered = siblings.filter(t3);
      if (colorFiltered.length > 0) {
        const visibleHandles = new Set(colorFiltered.map(_temp2));
        const alwaysVisible = siblings.filter(s_1 => (isBundleSibling(s_1) || s_1.keepAcrossColors) && !visibleHandles.has(s_1.handle));
        candidates = [...colorFiltered, ...alwaysVisible];
      }
    }
    const seen = new Set();
    t1 = candidates.filter(s_2 => {
      if (seen.has(s_2.productType)) {
        return false;
      }
      seen.add(s_2.productType);
      return true;
    });
    $[0] = selectedColor;
    $[1] = siblings;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const visibleSiblings = t1;
  if (visibleSiblings.length <= 1) {
    return null;
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="mb-3 block text-sm font-bold uppercase tracking-wide text-neutral-900">Style</span>;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== currentHandle || $[9] !== selectedColor || $[10] !== selectedOptions || $[11] !== visibleSiblings) {
    let t4;
    if ($[13] !== currentHandle || $[14] !== selectedColor || $[15] !== selectedOptions) {
      t4 = sibling => {
        const isCurrent = sibling.handle === currentHandle;
        const displayImage = getColorMatchedImage(sibling, selectedColor);
        const href = buildStyleHref(sibling.handle, sibling.variants, selectedOptions);
        return <Link key={sibling.handle} href={href} replace={true} scroll={false} className={`flex w-16 flex-col items-center overflow-hidden rounded-sm transition-colors md:w-[4.5rem] ${isCurrent ? "border border-neutral-900" : "border border-neutral-300 hover:border-neutral-400"}`}><div className="relative h-14 w-full overflow-hidden bg-neutral-100 md:h-16">{displayImage ? <ShopifyImg src={displayImage.url} alt={sibling.title} fill={true} className="object-cover" sizes="(max-width: 767px) 64px, 72px" /> : <div className="flex h-full items-center justify-center text-[10px] text-neutral-400">No image</div>}</div><span className="w-full truncate border-t border-neutral-100 py-1 text-center text-[9px] font-medium text-neutral-900 md:text-[10px]">{sibling.styleLabel ?? (sibling.productType === "Apparel Bundle" ? "Bundle" : sibling.productType || "Product")}</span></Link>;
      };
      $[13] = currentHandle;
      $[14] = selectedColor;
      $[15] = selectedOptions;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    t3 = visibleSiblings.map(t4);
    $[8] = currentHandle;
    $[9] = selectedColor;
    $[10] = selectedOptions;
    $[11] = visibleSiblings;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[17] !== t3) {
    t4 = <div>{t2}<div className="flex flex-wrap gap-3">{t3}</div></div>;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
}
function _temp2(s_0) {
  return s_0.handle;
}
function _temp(key) {
  return key.toLowerCase() === "color";
}